import { useEffect, useState } from 'react';
import { apiGet } from '../utils/api';
import { useDisguise } from '../context/DisguiseContext';


const TAG_COLOR = {
  '실적': '#388bfd',
  '계약': '#3b82f6',
  '유증': '#f85149',
  '지분': '#d2a8ff',
  '공시': '#8b949e',
};

// 폰트 사이즈 옵션 (S/M/L)
const NEWS_FONT_SIZE = {
  S: { title: 12, meta: 10, body: 11, tag: 10 },
  M: { title: 14, meta: 11, body: 13, tag: 11 },
  L: { title: 16, meta: 12, body: 15, tag: 12 },
};

// 밸류에이션 포맷 헬퍼
function fmtMcap(v) {           // hts_avls 단위: 억원
  const n = Number(v);
  if (!n || n <= 0) return '-';
  return n >= 10000 ? `${(n / 10000).toFixed(1)}조` : `${n.toLocaleString()}억`;
}
function fmtRatio(v) {          // 적자·리츠 등 null/0/음수 → '-'
  return (v == null || v <= 0) ? '-' : Number(v).toFixed(2);
}
function fmtQty(v) {            // 순매수 수량(주). 부호 유지, 만주 단위 축약
  if (v == null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const sign = n > 0 ? '+' : (n < 0 ? '-' : '');
  const a = Math.abs(n);
  if (a >= 10000) return `${sign}${(a / 10000).toFixed(1)}만`;
  return `${sign}${a.toLocaleString()}`;
}

// 3개월 일봉 차트 (SVG 캔들스틱)
function DailyChart({ data, disguised }) {
  if (!data || data.length === 0) return null;

  const W = 420, H = 120;
  const PAD = { t: 8, b: 18, l: 4, r: 4 };
  const chartW = W - PAD.l - PAD.r;
  const chartH = H - PAD.t - PAD.b;

  // 최신순 → 오래된순 (왼쪽=과거, 오른쪽=최신)
  const candles = [...data].reverse();
  const n = candles.length;
  if (n === 0) return null;

  const highs  = candles.map(c => c.high);
  const lows   = candles.map(c => c.low);
  const maxH   = Math.max(...highs);
  const minL   = Math.min(...lows);
  const range  = maxH - minL || 1;

  const yPos = (v) => PAD.t + chartH - ((v - minL) / range) * chartH;
  const candleW = Math.max(1, Math.floor(chartW / n) - 1);
  const xPos = (i) => PAD.l + i * (chartW / n) + (chartW / n - candleW) / 2;

  const upColor   = disguised ? '#9ca3af' : '#f85149';
  const downColor = disguised ? '#6b7280' : '#3b82f6';

  // 날짜 레이블 (첫/중간/마지막)
  const labelIdxs = [0, Math.floor(n / 2), n - 1];

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 3 }}>
        {disguised ? 'Price History (3M)' : '일봉 차트 (3개월)'}
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        {/* 배경 그리드 */}
        {[0.25, 0.5, 0.75].map(r => (
          <line key={r}
            x1={PAD.l} y1={PAD.t + chartH * r}
            x2={W - PAD.r} y2={PAD.t + chartH * r}
            stroke="#21262d" strokeWidth="1"
          />
        ))}

        {/* 캔들 */}
        {candles.map((c, i) => {
          const isUp  = c.close >= c.open;
          const color = isUp ? upColor : downColor;
          const bodyTop = yPos(Math.max(c.open, c.close));
          const bodyH   = Math.max(1, Math.abs(yPos(c.open) - yPos(c.close)));
          const cx      = xPos(i) + candleW / 2;

          return (
            <g key={i}>
              {/* 꼬리 */}
              <line x1={cx} y1={yPos(c.high)} x2={cx} y2={yPos(c.low)}
                stroke={color} strokeWidth="1" />
              {/* 몸통 */}
              <rect x={xPos(i)} y={bodyTop} width={candleW} height={bodyH}
                fill={color} />
            </g>
          );
        })}

        {/* 날짜 레이블 */}
        {labelIdxs.map(i => {
          if (i >= n) return null;
          const date = candles[i].date?.slice(5); // MM-DD
          const x = xPos(i) + candleW / 2;
          return (
            <text key={i} x={x} y={H - 2} textAnchor="middle"
              fontSize="9" fill="#8b949e">{date}</text>
          );
        })}
      </svg>
    </div>
  );
}


export default function NewsPanel({ selectedCode, selectedName }) {
  const { disguised, excel } = useDisguise();

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [dartOn, setDartOn]       = useState(true);
  const [tab, setTab]             = useState('news');
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [reports, setReports]     = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportScope, setReportScope]     = useState('stock');  // stock | mention
  const [sizeKey, setSizeKey]     = useState(
    () => localStorage.getItem('newspanel_fontsize') || 'M'
  );
  const fs = NEWS_FONT_SIZE[sizeKey];

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 600
  );
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth < 600); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  function cycleSize(next) {
    setSizeKey(next);
    localStorage.setItem('newspanel_fontsize', next);
  }

  useEffect(() => {
    if (!selectedCode) return;
    setLoading(true);
    apiGet(`/api/news/${selectedCode}?dart_enabled=${dartOn}&name=${encodeURIComponent(selectedName || '')}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [selectedCode, dartOn, selectedName]);

  // 일봉 차트 조회 (종목 변경 시)
  useEffect(() => {
    if (!selectedCode) { setChartData([]); return; }
    let cancelled = false;
    setChartLoading(true);
    apiGet(`/api/stock/chart/${selectedCode}?days=63`)
      .then(d => { if (!cancelled) { setChartData(Array.isArray(d) ? d : []); setChartLoading(false); } })
      .catch(() => { if (!cancelled) setChartLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCode]);

  // 종목분석 리포트 조회 (종목/검색범위 변경 시)
  useEffect(() => {
    if (!selectedCode) { setReports([]); return; }
    let cancelled = false;
    setReportLoading(true);
    apiGet(`/api/research/${selectedCode}?name=${encodeURIComponent(selectedName || '')}&scope=${reportScope}`)
      .then(d => { if (!cancelled) { setReports(Array.isArray(d?.items) ? d.items : []); setReportLoading(false); } })
      .catch(() => { if (!cancelled) setReportLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCode, selectedName, reportScope]);

  // 밸류에이션 지표 조회 (종목 변경 시) — /api/stock/price 재사용, 추가 KIS 호출 없음
  const [price, setPrice] = useState(null);
  useEffect(() => {
    if (!selectedCode) { setPrice(null); return; }
    let cancelled = false;
    apiGet(`/api/stock/price/${selectedCode}`)
      .then(d => { if (!cancelled) setPrice(d); })
      .catch(() => { if (!cancelled) setPrice(null); });
    return () => { cancelled = true; };
  }, [selectedCode]);

  // 외국인·기관 추정 순매수 (가집계) — 하루 4회 갱신, 서버 5분 캐시
  const [investor, setInvestor] = useState(null);
  useEffect(() => {
    if (!selectedCode) { setInvestor(null); return; }
    let cancelled = false;
    apiGet(`/api/stock/investor/${selectedCode}`)
      .then(d => { if (!cancelled) setInvestor(d); })
      .catch(() => { if (!cancelled) setInvestor(null); });
    return () => { cancelled = true; };
  }, [selectedCode]);

  const news        = data?.news        || [];
  const disclosures = data?.disclosures || [];
  const sources     = data?.sources     || {};

  // 위장 시 라벨
  const titleLabel    = disguised ? '📋 References' : '뉴스 · 공시';
  const loadingLabel  = disguised ? 'Loading...'    : '로딩 중...';
  const dartLabel     = disguised ? 'Aux'           : '공시';
  const newsTabLabel  = disguised ? `Ref ${news.length}`        : `뉴스 ${news.length}`;
  const dartTabLabel  = disguised ? `Aux ${disclosures.length}` : `공시 ${disclosures.length}`;
  const reportTabLabel = disguised ? `Rpt ${reports.length}`    : `리포트 ${reports.length}`;
  const emptyMessages = {
    selectStock:  disguised ? 'Select an item' : '종목을 선택하세요',
    noNews:       disguised ? 'No references'  : '뉴스가 없습니다',
    dartOff:      disguised ? 'Aux disabled'   : '공시가 꺼져 있습니다 (토글로 켜기)',
    noDart:       disguised ? 'No items'       : '오늘 공시가 없습니다',
  };

  return (
    <div style={{
      ...styles.panel,
      opacity: disguised ? 0.4 : 1,         // ← 위장 시 패널 전체 음영
      transition: 'opacity 0.2s',
    }}>
      {/* 헤더 */}
      <div style={styles.bar}>
        <span style={{
          ...styles.label,
          fontSize: isMobile ? 13 : 14,
          display: 'inline-flex', alignItems: 'center', gap: isMobile ? 4 : 6,
          whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          {!disguised && (
            <svg width={isMobile ? 14 : 17} height={isMobile ? 14 : 17} viewBox="0 0 24 24" fill="none" stroke="#a371f7" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M6 3 h9 l4 4 v14 a1 1 0 0 1 -1 1 H6 a1 1 0 0 1 -1 -1 V4 a1 1 0 0 1 1 -1 Z" />
              <polyline points="14,3 14,8 19,8" />
              <line x1="8" y1="12" x2="15" y2="12" />
              <line x1="8" y1="16" x2="15" y2="16" />
            </svg>
          )}
          {titleLabel}
        </span>
        {loading && !isMobile && <span style={styles.loading}>{loadingLabel}</span>}
        <label style={{ ...styles.toggle, marginLeft: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }} onClick={() => setDartOn(v => !v)}>
          <span style={{ fontSize: isMobile ? 12 : 14, color: '#8b949e' }}>{dartLabel}</span>
          <div style={{ ...styles.toggleBtn, background: dartOn ? '#388bfd' : '#30363d' }}>
            <div style={{ ...styles.toggleDot, left: dartOn ? 16 : 2 }} />
          </div>
          <span style={{ fontSize: isMobile ? 12 : 14, color: dartOn ? '#388bfd' : '#8b949e' }}>
            {dartOn ? 'ON' : 'OFF'}
          </span>
        </label>
        {!isMobile && (
          <div style={{ display: 'flex', gap: 3, marginLeft: 12 }}>
            {['S','M','L'].map(k => (
              <button key={k} onClick={() => cycleSize(k)} style={{
                fontSize: 14, padding: '2px 9px', borderRadius: 4, cursor: 'pointer',
                border: '1px solid #30363d',
                background: sizeKey === k ? '#388bfd' : '#21262d',
                color: sizeKey === k ? '#fff' : '#8b949e',
              }}>{k}</button>
            ))}
          </div>
        )}
      </div>

      {/* 밸류에이션 한 줄 — 위장/엑셀 모드에서는 숨김 (색상 없이 숫자만) */}
      {selectedCode && price && !disguised && !excel && (
        <div style={{ ...styles.valuation, fontSize: fs.body }}>
          {price.name && <span style={styles.valName}>{price.name}</span>}
          <span>시총 {fmtMcap(price.market_cap)}</span>
          <span style={styles.valSep}>·</span>
          <span>PER {fmtRatio(price.per)}</span>
          <span style={styles.valSep}>·</span>
          <span>PBR {fmtRatio(price.pbr)}</span>
          {price.w52_drop != null && (
            <>
              <span style={styles.valSep}>·</span>
              <span>고점대비 {price.w52_drop}%</span>
            </>
          )}
        </div>
      )}

      {/* 외국인·기관 추정 순매수 (가집계) — 확정치 아님을 명시 */}
      {selectedCode && investor && !disguised && !excel
        && (investor.frgn_qty != null || investor.orgn_qty != null) && (
        <div style={{ ...styles.investor, fontSize: fs.meta }}>
          <span style={styles.investorTag}>추정</span>
          <span>외국인 {fmtQty(investor.frgn_qty) ?? '-'}</span>
          <span style={styles.valSep}>·</span>
          <span>기관 {fmtQty(investor.orgn_qty) ?? '-'}</span>
        </div>
      )}

      {/* 3개월 일봉 차트 */}
      {selectedCode && chartLoading && (
        <div style={{ fontSize: 11, color: '#8b949e', textAlign: 'center', padding: '14px 0', background: '#161b22', borderRadius: 6, marginBottom: 8 }}>
          {disguised ? 'Loading chart...' : '차트 로딩 중...'}
        </div>
      )}
      {selectedCode && !chartLoading && chartData.length > 0 && (
        <DailyChart data={chartData} disguised={disguised} />
      )}
      {selectedCode && !chartLoading && chartData.length === 0 && (
        <div style={styles.candleEmpty}>{disguised ? 'No chart data' : '차트 데이터 없음'}</div>
      )}

      {/* 탭 */}
      <div style={styles.tabs}>
        {['news', 'dart', 'report'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            ...styles.tabBtn,
            background: tab === t ? '#388bfd' : '#21262d',
            color: tab === t ? '#fff' : '#8b949e',
          }}>
            {t === 'news' ? newsTabLabel : t === 'dart' ? dartTabLabel : reportTabLabel}
          </button>
        ))}
      </div>

      {/* 내용 */}
      <div style={styles.content}>
        {tab === 'news' && (
          <>
            {!selectedCode
              ? <Empty text={emptyMessages.selectStock} />
              : news.length === 0
                ? <Empty text={emptyMessages.noNews} />
                : news.map((item, i) => (
                  <div key={i} style={styles.newsRow}>
                    <a href={item.url} target="_blank" rel="noreferrer" style={{ ...styles.newsLink, fontSize: fs.body }}>
                      {item.title}
                    </a>
                    <div style={{ ...styles.date, fontSize: fs.meta }}>{fmtNewsTime(item.published_at, item.time_imprecise)}</div>
                  </div>
                ))
            }
            {news.length > 0 && !disguised && <Source text={sources.news} />}
          </>
        )}

        {tab === 'dart' && (
          <>
            {!dartOn
              ? <Empty text={emptyMessages.dartOff} />
              : !selectedCode
                ? <Empty text={emptyMessages.selectStock} />
                : disclosures.length === 0
                  ? <Empty text={emptyMessages.noDart} />
                  : disclosures.map((item, i) => (
                    <div key={i} style={styles.newsRow}>
                      <div style={styles.dartRow}>
                        <span style={{
                          ...styles.tag,
                          fontSize: fs.tag,
                          background: (TAG_COLOR[item.category] || '#8b949e') + '22',
                          color: TAG_COLOR[item.category] || '#8b949e',
                        }}>
                          {item.category}
                        </span>
                        <a href={item.url} target="_blank" rel="noreferrer" style={{ ...styles.newsLink, fontSize: fs.body }}>
                          {item.title}
                        </a>
                      </div>
                      <div style={{ ...styles.date, fontSize: fs.meta }}>{item.corp_name} · {item.filed_at}</div>
                    </div>
                  ))
            }
            {!disguised && <Source text={sources.disclosure || '출처: 금융감독원 전자공시시스템 DART (dart.fss.or.kr)'} />}
          </>
        )}

        {tab === 'report' && (
          <>
            {/* 검색범위 토글: 종목(itemCode) / 제목·내용(keyword) */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              {[['stock', disguised ? 'Item' : '종목'], ['mention', disguised ? 'Text' : '제목·내용']].map(([k, lbl]) => (
                <button key={k} onClick={() => setReportScope(k)} style={{
                  fontSize: fs.tag, padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
                  border: '1px solid #30363d',
                  background: reportScope === k ? '#388bfd' : '#21262d',
                  color: reportScope === k ? '#fff' : '#8b949e',
                }}>{lbl}</button>
              ))}
              {reportLoading && <span style={{ fontSize: fs.meta, color: '#8b949e', alignSelf: 'center' }}>{loadingLabel}</span>}
            </div>

            {!selectedCode
              ? <Empty text={emptyMessages.selectStock} />
              : reports.length === 0 && !reportLoading
                ? <Empty text={disguised ? 'No reports' : '리포트가 없습니다'} />
                : reports.map((r, i) => {
                  // PC: 읽기 페이지. 모바일: 네이버 모바일 리포트별 페이지(코드/nid). 둘 다 정확한 그 리포트로.
                  const href = (isMobile && r.code && r.nid)
                    ? `https://m.stock.naver.com/domestic/stock/${r.code}/research/${r.nid}`
                    : isMobile
                      ? `https://m.stock.naver.com/domestic/stock/${r.code || selectedCode}/research`
                      : r.url;
                  return (
                    <div key={i} style={styles.newsRow}>
                      <a href={href} target="_blank" rel="noreferrer" style={{ ...styles.newsLink, fontSize: fs.body }}>
                        {r.title}
                      </a>
                      <div style={{ ...styles.date, fontSize: fs.meta }}>
                        {[r.broker, r.date].filter(Boolean).join(' · ')}
                        {reportScope === 'mention' && r.stock ? ` · ${r.stock}` : ''}
                      </div>
                    </div>
                  );
                })
            }
            {reports.length > 0 && !disguised && (
              <Source text="출처: 네이버 금융 종목분석 리포트" />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div style={{ color: '#8b949e', fontSize: 12, textAlign: 'center', padding: 20 }}>{text}</div>;
}

function fmtNewsTime(raw, imprecise) {
  if (!raw) return '';
  const s = String(raw).trim();
  let y, mo, d, h, mi;

  let m = s.match(/(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})/);
  if (m) {
    [, y, mo, d, h, mi] = m;
  } else {
    const MON = { Jan:1,Feb:2,Mar:3,Apr:4,May:5,Jun:6,Jul:7,Aug:8,Sep:9,Oct:10,Nov:11,Dec:12 };
    const r = s.match(/(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})\s+(\d{1,2}):(\d{2})/);
    if (r) {
      d = r[1]; mo = MON[r[2]]; y = r[3]; h = r[4]; mi = r[5];
    } else {
      const dOnly = s.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (dOnly) { [, y, mo, d] = dOnly; h = null; mi = null; }
      else return s;
    }
  }

  const pad = (n) => String(n).padStart(2, '0');
  const now = new Date();
  const sameYear = Number(y) === now.getFullYear();
  const dateStr = sameYear ? `${pad(mo)}-${pad(d)}` : `${String(y).slice(2)}-${pad(mo)}-${pad(d)}`;
  if (imprecise || h == null) return dateStr;
  return `${dateStr} ${pad(h)}:${pad(mi)}`;
}

function Source({ text }) {
  if (!text) return null;
  return (
    <div style={{ marginTop: 10, padding: '5px 8px', borderRadius: 4, background: '#161b22', fontSize: 11, color: '#8b949e', borderLeft: '2px solid #30363d' }}>
      {text}
    </div>
  );
}

const styles = {
  panel: { height: '100%', display: 'flex', flexDirection: 'column' },
  valuation: {
    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
    color: '#c9d1d9',
    padding: '5px 2px 8px', marginBottom: 4,
    borderBottom: '1px solid #21262d',
  },
  valName: { color: '#e6edf3', fontWeight: 600, marginRight: 2 },
  valSep:  { color: '#30363d' },
  investor: {
    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
    color: '#8b949e',
    padding: '0 2px 8px', marginTop: -2, marginBottom: 4,
  },
  investorTag: {
    fontSize: 10, color: '#8b949e', border: '1px solid #30363d',
    borderRadius: 3, padding: '0 4px', lineHeight: '15px', flexShrink: 0,
  },
  candleEmpty: { fontSize: 12, color: '#8b949e', textAlign: 'center', padding: '14px 0', marginBottom: 8, background: '#161b22', borderRadius: 6 },
  bar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #21262d' },
  label: { fontWeight: 700, fontSize: 14 },
  loading: { fontSize: 12, color: '#8b949e' },
  toggle: { display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto', cursor: 'pointer' },
  toggleBtn: { width: 30, height: 16, borderRadius: 8, position: 'relative', transition: 'background 0.2s' },
  toggleDot: { position: 'absolute', top: 2, width: 12, height: 12, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' },
  tabs: { display: 'flex', gap: 6, marginBottom: 8 },
  tabBtn: { fontSize: 12, padding: '3px 10px', borderRadius: 4, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap' },
  content: { flex: 1, overflowY: 'auto', minHeight: 0 },
  newsRow: { borderBottom: '1px solid #21262d', padding: '7px 2px' },
  newsLink: { color: '#c9d1d9', textDecoration: 'none', fontSize: 14, lineHeight: 1.6, overflowWrap: 'anywhere', wordBreak: 'break-word', display: 'block' },
  date: { fontSize: 13, color: '#8b949e', marginTop: 3 },
  dartRow: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 },
  tag: { fontSize: 11, padding: '1px 6px', borderRadius: 3, fontWeight: 700, whiteSpace: 'nowrap' },
};
